import { useEffect } from "react";

import { useCookieConsent } from "@/hooks/useCookieConsent";
import { useAdsConversionSharingCountry } from "@/lib/adsConversionSharing";
import { isGoogleAdsRolloutCountry } from "@/lib/googleAdsClickId";
import {
  initializeGoogleAdsMeasurement,
  setGoogleAdsMeasurementEnabled,
} from "@/lib/googleAdsMeasurement";

export function GoogleAdsTag({
  tagId,
  accountCreationLabel,
  countryCode,
}: {
  tagId: string;
  accountCreationLabel: string;
  countryCode?: string;
}) {
  const {
    isAuthenticated,
    isConsentRequired,
    isConsentResolved,
    isGlobalPrivacyControlEnabled,
    marketingConsent,
    marketingConsentEnabled,
  } = useCookieConsent();
  const isRolloutCountry = isGoogleAdsRolloutCountry(countryCode);
  const shouldShareAllConversions = useAdsConversionSharingCountry("google", countryCode);
  const isRequiredConsentPending =
    !isAuthenticated &&
    isConsentRequired === true &&
    marketingConsent == null &&
    !isGlobalPrivacyControlEnabled;
  const isMeasurementConsentResolved =
    isConsentResolved &&
    (isAuthenticated || isConsentRequired !== undefined) &&
    !isRequiredConsentPending;
  const shouldClearAttribution =
    !isRolloutCountry ||
    isGlobalPrivacyControlEnabled ||
    (isMeasurementConsentResolved && !marketingConsentEnabled);
  const isMeasurementStateResolved = isMeasurementConsentResolved || shouldClearAttribution;
  const measurementEnabled =
    isRolloutCountry && isMeasurementConsentResolved && marketingConsentEnabled;

  useEffect(() => {
    setGoogleAdsMeasurementEnabled(measurementEnabled, {
      consentResolved: isMeasurementStateResolved,
      shouldClearAttribution,
      shouldShareAllConversions,
    });
    return () =>
      setGoogleAdsMeasurementEnabled(false, {
        consentResolved: isMeasurementStateResolved,
        shouldClearAttribution,
      });
  }, [
    isMeasurementStateResolved,
    measurementEnabled,
    shouldClearAttribution,
    shouldShareAllConversions,
  ]);

  useEffect(() => {
    if (!measurementEnabled || !tagId || !accountCreationLabel) {
      return;
    }

    initializeGoogleAdsMeasurement({ tagId, accountCreationLabel });
  }, [accountCreationLabel, measurementEnabled, tagId]);

  return null;
}
